import { TICKET_BANDS } from "@/lib/profileCompleteness";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { callProfileBuilder, getProfileCached, forgetProfile } from "@/lib/profileBuilderApi";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { splitChips } from "@/components/ui/chip-input";
import { StepShell, Question, ChoiceRow, PillToggle, BuildingState } from "@/components/campaign-strategy/parts";
import { Loader2, Upload, FileText, X, CheckCircle2, Trash2, Plus, Circle, Globe, Linkedin, Sparkles, Building2, Package, Users, MapPin, Award, SlidersHorizontal, ClipboardList, AlertCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { profileCompleteness, nameFromWebsite } from "@/lib/profileCompleteness";

interface Props {
  profileId: string;
  /** Brand-new profile: nothing saved yet, so skip the initial load. */
  isNew?: boolean;
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestToken?: string;
  onSaved?: () => void;
}

type Phase = "sources" | "reading" | "screens" | "summary";
type ScreenId = "about" | "sell" | "buyers" | "where" | "proof" | "last";
interface UploadedFile { path: string; name: string; mimeType: string }
interface Product { name: string; description: string; source: string; ticket_size?: string }
interface Facts {
  company_name: string; summary: string; business_model: string; products: Product[];
  industries_served: string[]; client_names: string[]; certifications: string[]; locations: string[];
  supply_regions: string[]; buyer_titles: string[]; proof_points: string[];
  typical_deal_size: string; company_size: string; website_url: string;
  sources_read?: string[]; sources_failed?: string[];
}

const ACCEPT = "application/pdf,image/png,image/jpeg,image/webp,text/plain";
const SUGGEST = {
  buyer_titles: ["Founder", "CEO", "Purchase Head", "Procurement Manager", "Operations Head", "CTO"],
  industries_served: ["Manufacturing", "Automotive", "IT & Software", "Healthcare", "BFSI", "Logistics"],
  supply_regions: ["India", "Middle East", "USA", "UK", "Europe", "Global"],
  certifications: ["ISO 9001", "ISO 27001", "ISO 14001", "SOC 2", "CE", "GMP"],
  never: ["Students", "Recruiters", "Freelancers"],
};
const COMPANY_SIZES = ["1-10 employees", "11-50 employees", "51-200 employees", "201-500 employees", "501-1000 employees", "1001-5000 employees", "5000+ employees"];
const DEAL_BANDS: Record<string, string[]> = {
  "₹": ["Under ₹50,000", "₹50,000 – ₹2 lakh", "₹2 – 10 lakh", "₹10 – 50 lakh", "₹50 lakh – ₹1 crore", "Over ₹1 crore"],
  "$": ["Under $1,000", "$1,000 – $5,000", "$5,000 – $25,000", "$25,000 – $100,000", "Over $100,000"],
};
const SCREENS: { id: ScreenId; title: string; help: string; icon: typeof Building2 }[] = [
  { id: "about", title: "About your company", help: "Check the name and a one-line description.", icon: Building2 },
  { id: "sell", title: "What do you sell?", help: "Your main products or services.", icon: Package },
  { id: "buyers", title: "Who buys from you?", help: "The industries and people who buy from you.", icon: Users },
  { id: "where", title: "Where do you work?", help: "Your offices and the places you can serve.", icon: MapPin },
  { id: "proof", title: "Why trust you?", help: "Clients and achievements make messages more believable.", icon: Award },
  { id: "last", title: "A few last details", help: "A few final answers. All optional.", icon: SlidersHorizontal },
];
const withTickets = (f: Facts): Facts => {
  const d = f?.typical_deal_size ?? "";
  const legacy = TICKET_BANDS.includes(d) ? d : "";
  return { ...f, products: (f?.products ?? []).map((p) => ({ ...p, ticket_size: p.ticket_size || legacy || undefined })) };
};
const TOTAL_STEPS = SCREENS.length + 2; // sources + screens + summary

const has = (v: unknown) => (Array.isArray(v) ? v.length > 0 : typeof v === "string" && v.trim().length > 0);
const draftKey = (id: string) => `cpb-draft-${id}`;

const call = callProfileBuilder;

function Field({ label, hint, ai, children }: { label: string; hint?: string; ai?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        {ai && <span className="inline-flex items-center gap-1 rounded-full bg-sage-muted px-2 py-0.5 text-[10px] font-medium text-sage"><Sparkles className="h-3 w-3" /> AI found this</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function CompanyProfileBuilderDialog({ profileId, isNew, workspaceId, open, onOpenChange, guestToken, onSaved }: Props) {
  const qc = useQueryClient();
  const base = { workspaceId: guestToken ? null : workspaceId, guestToken: guestToken ?? null, profileId };
  const [phase, setPhase] = useState<Phase>("sources");
  const [screen, setScreen] = useState(0);
  const [loading, setLoading] = useState(true);
  const [website, setWebsite] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [facts, setFacts] = useState<Facts | null>(null);
  const [gaps, setGaps] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [readingPhase, setReadingPhase] = useState(0);
  const [pendingDraft, setPendingDraft] = useState<{ facts: Facts; gaps: Record<string, string> } | null>(null);
  const [currency, setCurrency] = useState<"₹" | "$">("₹");
  const [showDescribe, setShowDescribe] = useState(false);
  const [aiKeys, setAiKeys] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);
  const [openSource, setOpenSource] = useState<string | null>(null);
  const [openProduct, setOpenProduct] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPendingDraft(null);
    setScreen(0);
    const readDraft = () => {
      try {
        const raw = localStorage.getItem(draftKey(profileId));
        if (raw) { const d = JSON.parse(raw); if (d?.facts) setPendingDraft(d); }
      } catch { /* ignore */ }
    };
    if (isNew) {
      setWebsite(""); setLinkedin(""); setDescription(""); setShowDescribe(false); setFiles([]); setGaps({}); setFacts(null);
      setPhase("sources"); readDraft(); setLoading(false);
      return;
    }
    getProfileCached(base)
      .then(async (p) => {
        // Safety net: an old-format profile with content is converted on open so its details show.
        if (!p.extracted_facts && !guestToken && p.has_legacy) {
          try {
            await supabase.functions.invoke("company-profile-builder", { body: { action: "convert_legacy", kind: "company", id: profileId, dryRun: false } });
            forgetProfile(profileId);
            p = await getProfileCached(base, true);
          } catch { /* fall through to sources */ }
        }
        const s = p.source_inputs ?? {};
        setWebsite(s.websiteUrl ?? ""); setLinkedin(s.linkedinUrl ?? ""); setDescription(s.description ?? "");
        setShowDescribe(!!(s.description ?? "").trim());
        setFiles(Array.isArray(s.files) ? s.files : []);
        setGaps(p.gap_answers ?? {});
        if (p.extracted_facts) {
          setFacts(withTickets(p.extracted_facts)); setPhase("summary");
          if (String(p.extracted_facts.typical_deal_size ?? "").includes("$")) setCurrency("$");
        } else setPhase("sources");
        readDraft();
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profileId]);

  // Autosave edits so closing the dialog never loses work.
  useEffect(() => {
    if (!facts || loading || pendingDraft || (phase !== "screens" && phase !== "summary")) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(draftKey(profileId), JSON.stringify({ facts, gaps, at: Date.now() })); } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [facts, gaps, phase, loading, pendingDraft, profileId]);

  const readingSteps = useMemo(() => [
    website && "Reading your website",
    linkedin && "Reading your LinkedIn page",
    files.length > 0 && `Reading ${files.length} file${files.length > 1 ? "s" : ""}`,
    description && "Reading your description",
    "Organising what we found",
  ].filter(Boolean) as string[], [website, linkedin, files.length, description]);

  useEffect(() => {
    if (phase !== "reading") return;
    setReadingPhase(0);
    const t = setInterval(() => setReadingPhase((p) => Math.min(p + 1, readingSteps.length - 1)), 9000);
    return () => clearInterval(t);
  }, [phase, readingSteps.length]);

  const handleFiles = async (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list).slice(0, Math.max(0, 5 - files.length));
    if (list.length > picked.length) toast.info("Up to 5 files per profile");
    setUploading(true);
    try {
      for (const f of picked) {
        const mimeType = f.type || (f.name.endsWith(".txt") ? "text/plain" : "");
        if (f.size > 20 * 1024 * 1024) { toast.error(`${f.name} is over 20 MB — try a smaller version`); continue; }
        if (!ACCEPT.split(",").includes(mimeType)) { toast.error(`${f.name}: please upload a PDF, image or text file`); continue; }
        const { path, token } = await call({ ...base, action: "upload_url", fileName: f.name, mimeType, size: f.size });
        const { error } = await supabase.storage.from("company-assets").uploadToSignedUrl(path, token, f, { contentType: mimeType });
        if (error) { toast.error(`${f.name} didn't upload — please try again`); continue; }
        setFiles((prev) => [...prev, { path, name: f.name, mimeType }]);
      }
    } catch (e: any) { toast.error(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeFile = (f: UploadedFile) => {
    setFiles((prev) => prev.filter((x) => x.path !== f.path));
    call({ ...base, action: "remove_file", path: f.path }).catch(() => undefined);
  };

  const extract = async () => {
    if (!website.trim() && !linkedin.trim() && !description.trim() && files.length === 0) {
      toast.error("Add a website, LinkedIn page, a file, or a short description");
      return;
    }
    setPhase("reading");
    try {
      forgetProfile(profileId);
      const res = await call({ ...base, action: "extract", websiteUrl: website, linkedinUrl: linkedin, description, files });
      const f = { ...res.facts };
      if (!String(f.company_name ?? "").trim()) f.company_name = nameFromWebsite(website || linkedin.replace(/.*company\//i, ""));
      setFacts(withTickets(f));
      setAiKeys(new Set(Object.keys(f).filter((k) => has((f as any)[k]))));
      setPendingDraft(null);
      try { localStorage.removeItem(draftKey(profileId)); } catch { /* ignore */ }
      if (res.sourcesFailed?.length) toast.warning(`We couldn't open: ${res.sourcesFailed.join(", ")}. You can fill in the gaps on the next screens.`);
      setScreen(0);
      setPhase("screens");
    } catch (e: any) {
      toast.error(e.message);
      setPhase(facts ? "summary" : "sources");
    }
  };

  const save = async () => {
    if (!facts) return;
    // Keep gap answers in sync with what the customer edited on the screens.
    const answers = {
      ...gaps,
      best_customers: (facts.client_names ?? []).join(", "),
      buyer_titles: (facts.buyer_titles ?? []).join(", "),
      supply_regions: (facts.supply_regions ?? []).join(", "),
      deal_size: facts.typical_deal_size ?? "",
    };
    setSaving(true);
    try {
      const named = facts.company_name?.trim() ? facts : { ...facts, company_name: nameFromWebsite(website) || "My company" };
      await call({ ...base, action: "save", facts: named, gapAnswers: answers });
      forgetProfile(profileId);
      try { localStorage.removeItem(draftKey(profileId)); } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["company-knowledge-profiles"] });
      qc.invalidateQueries({ queryKey: ["company-knowledge-profiles-scores", workspaceId] });
      qc.invalidateQueries({ queryKey: ["company-profile-facts-status", profileId] });
      qc.invalidateQueries({ queryKey: ["company-knowledge-profile"] });
      toast.success("Company profile saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const update = <K extends keyof Facts>(k: K, v: Facts[K]) => setFacts((f) => (f ? { ...f, [k]: v } : f));

  // ---------- Progress ----------
  const stepNo = phase === "sources" || phase === "reading" ? 1 : phase === "screens" ? screen + 2 : TOTAL_STEPS;
  const progress = Math.round((stepNo / TOTAL_STEPS) * 100);

  const header = (() => {
    if (phase === "sources") return { title: "Tell us about your business", help: "Share your website, LinkedIn page or brochure. We'll read it and fill in your profile." };
    if (phase === "reading") return { title: "Reading your sources", help: "This usually takes 30–60 seconds." };
    if (phase === "summary") return { title: "Your company profile", help: "Check everything once, then save." };
    return { title: SCREENS[screen].title, help: SCREENS[screen].help };
  })();

  const goNext = () => (screen < SCREENS.length - 1 ? setScreen(screen + 1) : setPhase("summary"));
  const goBack = () => (screen > 0 ? setScreen(screen - 1) : setPhase(facts ? "summary" : "sources"));

  // ---------- Screens ----------
  const Label2 = ({ children }: { children: React.ReactNode }) => <p className="mb-3 text-center text-sm text-muted-foreground">{children}</p>;
  const pills = (key: keyof Facts, opts: string[] = [], addLabel = "Add") => (
    <PillToggle options={opts.map((v) => ({ v, label: v }))} value={(facts?.[key] as string[]) ?? []} onChange={(v) => update(key, v as any)} addLabel={addLabel} />
  );

  const renderScreen = (id: ScreenId) => {
    if (!facts) return null;
    switch (id) {
      case "about": return (
        <div className="space-y-5">
          <Input value={facts.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="Company name"
            className="h-14 rounded-xl text-center text-lg font-medium" />
          <Textarea rows={4} value={facts.summary} onChange={(e) => update("summary", e.target.value)} placeholder="What you do, in one or two sentences" className="rounded-xl" />
        </div>
      );
      case "sell": {
        const setP = (i: number, patch: Partial<Product>) => update("products", facts.products.map((x, j) => (j === i ? { ...x, ...patch } : x)));
        const list = facts.products ?? [];
        return (
          <div className="space-y-2">
            {list.map((p, i) => (
              <div key={i} className="space-y-3 rounded-xl border border-border p-4">
                <Input value={p.name} placeholder="Product or service name" onChange={(e) => setP(i, { name: e.target.value })} />
                <PillToggle single noAdd className="justify-start" options={TICKET_BANDS.map((b) => ({ v: b, label: b }))}
                  value={p.ticket_size ? [p.ticket_size] : []} onChange={(v) => setP(i, { ticket_size: v[0] })} />
                <Textarea rows={2} value={p.description ?? ""} placeholder="About this product (optional)" onChange={(e) => setP(i, { description: e.target.value })} />
                <button type="button" onClick={() => update("products", list.filter((_, j) => j !== i))}
                  className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
              </div>
            ))}
            {list.length < 5 && (
              <button type="button" onClick={() => { update("products", [...list, { name: "", description: "", source: "added by you" }]); setOpenProduct(list.length); }}
                className="flex h-14 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground">
                <Plus className="h-4 w-4" /> Add product
              </button>
            )}
          </div>
        );
      }
      case "buyers": return (
        <div className="space-y-8">
          <div><Label2>Industries</Label2>{pills("industries_served", SUGGEST.industries_served, "Industry")}</div>
          <div><Label2>Job titles</Label2>{pills("buyer_titles", SUGGEST.buyer_titles, "Title")}</div>
        </div>
      );
      case "where": return (
        <div className="space-y-8">
          <div><Label2>Your locations</Label2>{pills("locations", [], "City")}</div>
          <div><Label2>Where you can serve</Label2>{pills("supply_regions", SUGGEST.supply_regions, "Region")}</div>
        </div>
      );
      case "proof": return (
        <div className="space-y-8">
          <div><Label2>Clients</Label2>{pills("client_names", [], "Client")}</div>
          <div><Label2>Certifications</Label2>{pills("certifications", SUGGEST.certifications, "Certification")}</div>
          <div><Label2>Achievements</Label2>{pills("proof_points", [], "Achievement")}</div>
        </div>
      );
      case "last": return (
        <div className="space-y-8">
          <div><Label2>Your company size</Label2>
            <PillToggle single noAdd options={COMPANY_SIZES.map((v) => ({ v, label: v.replace(" employees", "") }))}
              value={facts.company_size ? [facts.company_size] : []} onChange={(v) => update("company_size", v[0] ?? "")} />
          </div>
        </div>
      );
    }
  };

  // ---------- Summary ----------
  const summaryRows: { label: string; value: string; screen: number }[] = facts ? [
    { label: "About", value: [facts.company_name, facts.summary].filter(Boolean).join(" — "), screen: 0 },
    { label: "Products", value: (facts.products ?? []).filter((p) => p.name).map((p) => p.name).join(", "), screen: 1 },
    { label: "Sells to", value: [...(facts.industries_served ?? []), ...(facts.buyer_titles ?? [])].join(", "), screen: 2 },
    { label: "Where", value: [...(facts.locations ?? []), ...(facts.supply_regions ?? [])].join(", "), screen: 3 },
    { label: "Proof", value: [...(facts.client_names ?? []), ...(facts.certifications ?? [])].join(", "), screen: 4 },
    { label: "Details", value: facts.company_size ?? "", screen: 5 },
  ] : [];
  const completeness = facts ? profileCompleteness(facts, gaps) : { score: 0, missing: [] };
  const hasSource = !!(website.trim() || linkedin.trim() || files.length || description.trim());
  const filledCount = summaryRows.filter((r) => !completeness.missing.some((m) => m.screen === r.screen)).length;

  // ---------- Footer ----------
  const wide = "h-12 w-full rounded-xl text-base";
  let footer: React.ReactNode = null;
  if (phase === "sources") footer = (
    <Button onClick={extract} disabled={uploading || !hasSource} className={cn(wide, "gap-2")}><Sparkles className="h-4 w-4" /> Read and build profile</Button>
  );
  else if (phase === "screens") footer = (
    <div className="space-y-2">
      <Button onClick={goNext} className={wide}>Continue</Button>
      {screen > 0 && <button type="button" onClick={goNext} className="w-full py-1 text-sm text-muted-foreground hover:text-foreground">Skip for now</button>}
    </div>
  );
  else if (phase === "summary") footer = (
    <div className="space-y-2">
      <Button onClick={save} disabled={saving} className={cn(wide, "gap-2")}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save profile</Button>
      <button type="button" onClick={() => setPhase("sources")} className="w-full py-1 text-sm text-muted-foreground hover:text-foreground">Change sources</button>
    </div>
  );

  const sourceRow = (key: string, icon: typeof Globe, label: string, filled: boolean, children: React.ReactNode, status?: string) => (
    <ChoiceRow active={openSource === key} onClick={() => setOpenSource(openSource === key ? null : key)} icon={filled && openSource !== key ? CheckCircle2 : icon} label={label}>
      {children}
      {status && facts?.sources_failed?.includes(status) && <p className="text-xs text-destructive">We couldn't read this last time. A brochure works better.</p>}
    </ChoiceRow>
  );

  const back = phase === "screens" ? goBack : phase === "sources" && facts ? () => setPhase("summary") : undefined;
  const current = phase === "sources" || phase === "reading" ? 0 : phase === "screens" ? screen + 1 : TOTAL_STEPS - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o && facts && !saving && (phase === "summary" || phase === "screens")
        && !window.confirm("Your profile is not saved yet. Leave without saving? (Your edits are kept as a draft.)")) return;
      onOpenChange(o);
    }}>
      <DialogContent className="flex h-[min(680px,92vh)] max-w-[560px] flex-col gap-0 overflow-hidden p-0 [&>div:last-of-type]:min-h-0 [&>div:last-of-type]:flex-1">
        <DialogTitle className="sr-only">{header.title}</DialogTitle>
        <DialogDescription className="sr-only">{header.help}</DialogDescription>
        <StepShell total={TOTAL_STEPS} current={current} onBack={loading ? undefined : back} footer={loading ? undefined : footer}>
          {pendingDraft && !loading && (
            <div className="mb-5 flex items-center justify-center gap-3 text-sm text-muted-foreground">
              <span>Continue where you left off?</span>
              <button type="button" className="font-medium text-primary hover:underline" onClick={() => { setFacts(pendingDraft.facts); setGaps(pendingDraft.gaps ?? {}); setPhase("summary"); setPendingDraft(null); }}>Resume</button>
              <span>·</span>
              <button type="button" className="hover:text-foreground" onClick={() => { localStorage.removeItem(draftKey(profileId)); setPendingDraft(null); }}>Discard</button>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : phase === "reading" ? (
            <BuildingState title="Reading your business…" lines={readingSteps} />
          ) : phase === "sources" ? (
            <Question title="Tell us about your business">
              <div className="space-y-2">
                {sourceRow("website", Globe, website.trim() || "Website", !!website.trim(),
                  <Input autoFocus value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.yourcompany.com" />, "website")}
                {sourceRow("linkedin", Linkedin, linkedin.trim() ? "LinkedIn page added" : "LinkedIn company page", !!linkedin.trim(),
                  <Input autoFocus value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="linkedin.com/company/yourcompany" />, "linkedin")}
                {sourceRow("files", FileText, files.length ? `${files.length} file${files.length > 1 ? "s" : ""} added` : "Brochure or catalogue", files.length > 0, (
                  <>
                    <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                    <button type="button" disabled={uploading || files.length >= 5} onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed py-4 text-sm text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload PDF, image or text
                    </button>
                    {files.map((f) => (
                      <div key={f.path} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 truncate">{f.name}</span>
                        <button type="button" onClick={() => removeFile(f)} aria-label={`Remove ${f.name}`} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </>
                ))}
                {showDescribe ? (
                  <Textarea rows={4} autoFocus value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-xl"
                    placeholder="e.g. We make industrial valves in Pune and sell to oil & gas plants across India." />
                ) : (
                  <button type="button" className="w-full pt-3 text-center text-sm text-muted-foreground hover:text-foreground" onClick={() => setShowDescribe(true)}>
                    I don't have these
                  </button>
                )}
              </div>
            </Question>
          ) : phase === "screens" ? (
            <Question key={screen} title={SCREENS[screen].title}>{renderScreen(SCREENS[screen].id)}</Question>
          ) : phase === "summary" && facts ? (
            <Question title={facts.company_name || "Your company profile"}>
              <p className="-mt-4 mb-5 text-center text-sm text-muted-foreground">{filledCount} of {summaryRows.length} filled</p>
              <div className="divide-y rounded-2xl border px-4">
                {summaryRows.map((r) => {
                  const miss = completeness.missing.some((m) => m.screen === r.screen);
                  return (
                    <button key={r.label} type="button" onClick={() => { setScreen(r.screen); setPhase("screens"); }}
                      className="flex w-full items-center gap-4 py-4 text-left">
                      <span className="w-20 shrink-0 text-sm text-muted-foreground">{r.label}</span>
                      <span className={cn("min-w-0 flex-1 truncate font-medium", miss && "text-destructive")}>{r.value || "Add"}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            </Question>
          ) : null}
        </StepShell>
      </DialogContent>
    </Dialog>
  );
}
